import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { v4 as uuidV4, NIL } from "uuid";
import { RootState, useAppDispatch } from "store";
import { addOrUpdateToBasket, ProductBasket } from "store/bookingJourney";
import { ADD_UPDATE_DELETE_EVENTS } from "utils/graphql/bookingJourney/booking-journey-queries";
import { Booking, Event, EventDetail } from "utils/graphql/bookingJourney/models";
import { CreationMethod } from "utils/types";
import { COUNTRIES, DEFAULT_CURRENCY, EVENTSTEXT, OFF_THE_SHELF_PRODUCT } from "utils/constants";
import {
  checkIsVirtual,
  constructBookingTypeFromProductType,
  constructVenueFromProductType,
  ErrorToastPayload,
  populateProductDetailsFromEvents,
  venueType,
} from "utils/helpers";

// Common fields required to add an event (new or repeat booking) to the basket.
export interface AddToBasketInput {
  courseId: string;
  title: string;
  image?: string;
  customerProductCode: string;
  bookingType: string;
  deliveryMethod: string;
  eventCategory: string;
  workshopLength: string | number;
  minimumDelegates: number;
  maximumDelegates: number;
  cost: number;
  currency: string;
  activityId: string;
  priceId: string;
  quantity: number;
}

/**
 * Shared hook to raise events from the booking journey and add the resulting
 * product to the basket. Used by course booking (CourseV2) and "Book again" flows.
 */
export const useAddToBasket = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const bookingId = useSelector((state: RootState) => state.bookingJourney.basket.bookingId) || NIL;

  const [addEventsMutation, { loading }] = useMutation(ADD_UPDATE_DELETE_EVENTS);

  const addToBasket = useCallback(
    (input: AddToBasketInput, alertTitle: string) => {
      const events: Partial<Event>[] = Array.from({ length: input.quantity }, () => ({
        country: COUNTRIES.UK,
        sessionMaxCapacity: input.maximumDelegates,
        sessionMinCapacity: input.minimumDelegates,
        anyNonStandardRequirements: false,
        contextualisationRequired: false,
        subscribeToNewsLetter: true,
        isDeleted: false,
        isProgramme: false,
        duration: input.workshopLength as string,
        eventChannel: input.deliveryMethod,
        eventCategory: input.eventCategory,
        topicName: input.title,
        cost: input.cost,
        topicId: input.customerProductCode,
        team: EVENTSTEXT,
        virtualDelivery: checkIsVirtual(input.deliveryMethod),
        location: null,
        eventNotes: null,
        creationMethod: CreationMethod.Ibf,
        courseId: input.courseId,
        venueType: venueType(input.eventCategory, checkIsVirtual(input.deliveryMethod)),
        eventSpecialised: OFF_THE_SHELF_PRODUCT,
        activityId: input.activityId,
        appianPriceId: input.priceId,
        currency: input.currency || DEFAULT_CURRENCY,
      }));

      const eventRequest: Booking = {
        bookingId,
        events,
        creationMethod: CreationMethod.Ibf,
      };

      addEventsMutation({ variables: { body: eventRequest } })
        .then(({ data }: any) => {
          const matchedEvents: EventDetail[] = data.addEventsFromBookingJourney.events.filter(
            (x) =>
              x.courseId === input.courseId &&
              x.cost === input.cost &&
              x.duration == input.workshopLength &&
              x.eventChannel == input.deliveryMethod &&
              x.sessionMaxCapacity === input.maximumDelegates &&
              x.sessionMinCapacity == input.minimumDelegates &&
              constructBookingTypeFromProductType(x.eventCategory) === input.bookingType,
          );

          const courseToBeAdded: ProductBasket = {
            id: uuidV4(),
            courseId: input.courseId,
            title: input.title,
            image: input.image,
            customerProductCode: input.customerProductCode,
            bookingType: constructBookingTypeFromProductType(matchedEvents[0].eventCategory),
            deliveryMethod: matchedEvents[0].eventChannel,
            venue: constructVenueFromProductType(matchedEvents[0].eventCategory),
            workshopLength: matchedEvents[0].duration,
            minimumDelegates: matchedEvents[0].sessionMinCapacity,
            maximumDelegates: matchedEvents[0].sessionMaxCapacity,
            quantity: input.quantity,
            price: matchedEvents[0].cost,
            subTotal: matchedEvents[0].cost * input.quantity,
            productDetails: populateProductDetailsFromEvents(matchedEvents),
            activityId: matchedEvents[0].activityId,
            priceId: matchedEvents[0].appianPriceId,
            currency: matchedEvents[0].currency || DEFAULT_CURRENCY,
            eventCategory: matchedEvents[0].eventCategory,
            isValid: true,
          };

          dispatch(
            addOrUpdateToBasket({
              bookingId: data.addEventsFromBookingJourney.bookingId,
              bookingNo: data.addEventsFromBookingJourney.bookingNo,
              courseToBeAdded,
            }),
          );
          dispatch({
            type: "alert/show",
            payload: {
              type: "success",
              title: alertTitle,
              message: t("basket_add_success"),
            },
          });
        })
        .catch(() => {
          dispatch({
            type: "alert/show",
            payload: ErrorToastPayload(alertTitle, t("basket_add_error")),
          });
        });
    },
    [addEventsMutation, bookingId, dispatch, t],
  );

  return { addToBasket, loading } as const;
};
